/** Conversation state for the Recovry AI widget. */
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';

import { aiApi } from '@/api/aiApi';
import { streamChat } from '@/api/aiStream';
import {
  ChatContext,
  type ChatContextValue,
  type ChatMessage,
  knownCta,
  type ChatState,
  type Emergency,
  type ServiceCard,
} from './chatContext';





type Action =
  | { type: 'open'; seed?: string }
  | { type: 'close' }
  | { type: 'draft'; value: string }
  | { type: 'user'; id: string; text: string }
  | { type: 'assistant'; id: string }
  | { type: 'token'; id: string; text: string }
  | { type: 'replace'; id: string; text: string }
  | { type: 'finish'; id: string; patch?: Partial<ChatMessage> }
  /** Mark a turn failed; keeps any text already streamed, else uses `text`. */
  | { type: 'fail'; id: string; text: string; cta?: ChatMessage['cta'] }
  /** Close out a turn ONLY if it is still streaming (stream ended abnormally). */
  | { type: 'settle'; id: string; text: string }
  | { type: 'session'; id: string }
  | { type: 'wall'; wall: ChatState['wall'] }
  | { type: 'streaming'; value: boolean }
  | { type: 'hydrate'; state: Partial<ChatState> }
  | { type: 'reset' };

const STORAGE_KEY = 'recovry_ai_session';

const initial: ChatState = {
  open: false,
  messages: [],
  sessionId: null,
  streaming: false,
  wall: null,
  draft: '',
};

function reducer(state: ChatState, action: Action): ChatState {
  switch (action.type) {
    case 'open':
      return { ...state, open: true, draft: action.seed ?? state.draft };
    case 'close':
      return { ...state, open: false };
    case 'draft':
      return { ...state, draft: action.value };
    case 'user':
      return {
        ...state,
        draft: '',
        wall: null,
        messages: [...state.messages, { id: action.id, role: 'user', text: action.text, status: 'done' }],
      };
    case 'assistant':
      return {
        ...state,
        streaming: true,
        messages: [...state.messages, { id: action.id, role: 'assistant', text: '', status: 'streaming' }],
      };
    case 'token':
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.id ? { ...m, text: m.text + action.text } : m,
        ),
      };
    case 'replace':
      return {
        ...state,
        messages: state.messages.map((m) => (m.id === action.id ? { ...m, text: action.text } : m)),
      };
    case 'finish':
      return {
        ...state,
        streaming: false,
        messages: state.messages.map((m) =>
          m.id === action.id ? { ...m, status: 'done', ...action.patch } : m,
        ),
      };
    case 'fail':
      return {
        ...state,
        streaming: false,
        messages: state.messages.map((m) =>
          m.id === action.id
            ? { ...m, status: 'error', text: m.text || action.text, cta: action.cta ?? m.cta ?? null }
            : m,
        ),
      };
    case 'settle':
      // Decided inside the reducer, on authoritative state: a `stateRef`
      // read from the async `finally` can be stale before React flushes.
      return {
        ...state,
        streaming: false,
        messages: state.messages.map((m) =>
          m.id === action.id && m.status === 'streaming'
            ? { ...m, status: 'error', text: m.text || action.text }
            : m,
        ),
      };
    case 'session':
      return { ...state, sessionId: action.id };
    case 'wall':
      return { ...state, wall: action.wall };
    case 'streaming':
      return { ...state, streaming: action.value };
    case 'hydrate':
      return { ...state, ...action.state };
    case 'reset':
      return { ...initial, open: state.open };
    default:
      return state;
  }
}



let counter = 0;
const nextId = () => `m${Date.now().toString(36)}_${counter++}`;

export function AiChatProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initial);
  const abortRef = useRef<AbortController | null>(null);
  const contextRef = useRef<Record<string, unknown> | null>(null);

  // Rehydrate from sessionStorage (not localStorage): the transcript contains described
  // symptoms and should not outlive the browser session on a shared or public machine.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw) as Partial<ChatState>;
        dispatch({ type: 'hydrate', state: { messages: saved.messages ?? [], sessionId: saved.sessionId ?? null } });
      }
    } catch {
      /* corrupt or unavailable — start fresh */
    }
  }, []);

  useEffect(() => {
    if (state.streaming) return;
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ messages: state.messages.slice(-40), sessionId: state.sessionId }),
      );
    } catch {
      /* quota or private mode — non-fatal */
    }
  }, [state.messages, state.sessionId, state.streaming]);

  // Never leave a stream open behind a closed panel or a unloading page.
  useEffect(() => {
    const onUnload = () => abortRef.current?.abort();
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      abortRef.current?.abort();
    };
  }, []);

  // The send closure needs the latest state without being recreated.
  const stateRef = useRef(state);
  stateRef.current = state;

  const send = useCallback((raw: string) => {
    const text = raw.trim();
    // Re-entrancy guard, matching the app.
    if (!text || stateRef.current.streaming) return;

    const userId = nextId();
    const assistantId = nextId();
    dispatch({ type: 'user', id: userId, text });
    dispatch({ type: 'assistant', id: assistantId });

    const controller = new AbortController();
    abortRef.current = controller;

    void (async () => {
      try {
        await streamChat(
          { message: text, sessionId: stateRef.current.sessionId, context: contextRef.current },
          {
            onEvent: ({ event, data }) => {
              const d = data as Record<string, unknown>;
              switch (event) {
                case 'session':
                  if (d.session_id) dispatch({ type: 'session', id: d.session_id as string });
                  break;
                case 'delta':
                  dispatch({ type: 'token', id: assistantId, text: (d.text as string) ?? '' });
                  break;
                case 'replace':
                  dispatch({ type: 'replace', id: assistantId, text: (d.text as string) ?? '' });
                  break;
                case 'emergency':
                  dispatch({ type: 'finish', id: assistantId, patch: { emergency: d as unknown as Emergency } });
                  break;
                case 'result':
                  dispatch({
                    type: 'finish',
                    id: assistantId,
                    patch: {
                      services: (d.services as ServiceCard[]) ?? [],
                      options: (d.options as string[]) ?? [],
                      disclaimer: d.disclaimer as string,
                      cta: knownCta(d.cta),
                    },
                  });
                  break;
                case 'error':
                  if (d.code === 'chat_quota_exhausted') {
                    dispatch({
                      type: 'wall',
                      wall: { message: (d.message as string) ?? '', loginRequired: Boolean(d.login_required) },
                    });
                  }
                  // Never a blank error bubble: keep streamed text, else explain.
                  dispatch({
                    type: 'fail',
                    id: assistantId,
                    text: (d.message as string) || 'Something went wrong. Please try again.',
                    // The server says where to go when it cannot help, e.g. the team.
                    cta: knownCta(d.cta),
                  });
                  break;
                case 'done':
                  dispatch({ type: 'streaming', value: false });
                  break;
                default:
                  break;
              }
            },
            onPreStreamError: (status, bodyData) => {
              if (bodyData.code === 'chat_quota_exhausted') {
                dispatch({
                  type: 'wall',
                  wall: {
                    message: (bodyData.message as string) ?? "You've used your free questions.",
                    loginRequired: Boolean(bodyData.login_required),
                  },
                });
                dispatch({ type: 'finish', id: assistantId, patch: { status: 'stopped' } });
                return;
              }
              dispatch({
                type: 'finish',
                id: assistantId,
                patch: {
                  status: 'error',
                  text:
                    (bodyData.error as string) ??
                    (status === 503
                      ? 'The assistant is busy right now. Please try again.'
                      : 'Something went wrong. Please try again.'),
                },
              });
            },
          },
          controller.signal,
        );
      } catch (err) {
        const aborted = (err as Error)?.name === 'AbortError';
        dispatch({
          type: 'finish',
          id: assistantId,
          patch: aborted
            ? { status: 'stopped' }
            : { status: 'error', text: 'Connection lost. Please try again.' },
        });
      } finally {
        abortRef.current = null;
        // A stream ending without a terminal event resolves normally (no throw),
        // so the catch never runs and this bubble would spin forever.
        dispatch({ type: 'settle', id: assistantId, text: 'Connection lost. Please try again.' });
      }
    })();
  }, []);

  const value = useMemo<ChatContextValue>(
    () => ({
      ...state,
      openChat: (opts) => {
        contextRef.current = opts?.context ?? null;
        dispatch({ type: 'open', seed: opts?.seed });
      },
      close: () => {
        abortRef.current?.abort();
        dispatch({ type: 'close' });
      },
      setDraft: (v) => dispatch({ type: 'draft', value: v }),
      send,
      stop: () => abortRef.current?.abort(),
      reset: () => {
        // Clearing here is a real deletion request, so erase the server copy
        // too -- unlike closing a tab, which must leave the audit trail alone.
        const sid = state.sessionId;
        if (sid) void aiApi.deleteSession(sid).catch(() => {});
        abortRef.current?.abort();
        try {
          sessionStorage.removeItem(STORAGE_KEY);
        } catch {
          /* non-fatal */
        }
        dispatch({ type: 'reset' });
      },
    }),
    [state, send],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

